import React, { useState } from "react";
import styles from "./widget-picker-modal.module.scss";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@src/reducers/store";
import { GlassyModal } from "../glassy-modal/glassy-modal";
import { GlassyModalHeader } from "@src/components/molecules/glassy-modal-header/glassy-modal-header";
import { PillNavigation } from "@src/components/atoms/pill-navigation/pill-navigation";
import closeIcon from "@src/assets/svg/close-icon.svg";
import Spinner from "@src/components/atoms/spinner/spinner";
import {
  updateModalsCloseDrawerModal,
  updateEditWidgetDrawerModalTileId,
} from "@src/reducers/your-reducer";
import { useI18N } from "@src/language/language-context";
import {
  WIDGET_LIBRARY_CATEGORIES,
  WidgetLibraryCategory,
  WIDGET_LIBRARY_DEFS,
} from "@src/components/organisms/widget-library-panel/widget-library-registry";
import { Widget, WidgetTemplate } from "@src/types/dashboards";
import { useWidgetTemplates } from "@src/components/organisms/widget-library-panel/use-widget-templates";
import { WidgetRenderer } from "@src/components/organisms/widgets/widget-renderer";
import { getWidgetSize } from "@src/components/organisms/widgets/widget-sizes";
import { findFreeSlot } from "@src/components/organisms/widgets/find-free-slot";
import { addPendingWidget } from "@src/reducers/dashboards-slice";

const cssClassName = "widget-picker-modal";

/**
 * Doc §2.3 WidgetPickerModal — lists active widget templates (T1) grouped by
 * category as live previews; clicking a tile adds that template to the selected
 * dashboard (B5, add-widget-from-template) with placeholder values.
 */
export const WidgetPickerModal = () => {
  const dispatch = useDispatch();
  const i18n = useI18N();
  const [category, setCategory] = useState<WidgetLibraryCategory>("all");
  const [isAdding, setIsAdding] = useState(false);
  const selectedDashboard = useSelector((state: RootState) =>
    state.dashboards.dashboards.find(
      (d) => d.id === state.dashboards.selectedDashboardId,
    ),
  );

  const { templates, isLoading, error } = useWidgetTemplates();

  const title = i18n("pages.dashboardsPage.widgetLibrary");
  const categoryLabel: Record<WidgetLibraryCategory, string> = {
    all: i18n("pages.dashboardsPage.categoryAll"),
    status: i18n("pages.dashboardsPage.categoryStatus"),
    values: i18n("pages.dashboardsPage.categoryValues"),
    trends: i18n("pages.dashboardsPage.categoryTrends"),
    counters: i18n("pages.dashboardsPage.categoryCounters"),
  };

  // type -> category + display metadata (registry is the display map)
  const defByType = Object.fromEntries(
    WIDGET_LIBRARY_DEFS.map((d) => [d.type, d]),
  );

  const categoryTemplates =
    category === "all"
      ? templates.filter((t) => defByType[t.type])
      : templates.filter((t) => defByType[t.type]?.category === category);

  const handleClose = () => {
    dispatch(
      updateModalsCloseDrawerModal({
        showModal: false,
        id: "widgetPickerModal",
      }),
    );
  };

  // Add the chosen template to the selected dashboard. The widget is
  // inserted LOCALLY only (id `pending-{uuid}`, no backend round-trip). It
  // appears on the canvas immediately so the user sees their pick, and the
  // edit drawer opens on it. The actual commit happens in the drawer's Save
  // handler (POST /widgets/from-template + PUT /widgets/{realId} — see the
  // `isPendingWidget` branch in edit-widget-drawer-modal.tsx). If the user
  // cancels, the dispatch-red flag in `pendingWidgetIds` keeps the cleanup
  // tidy: handleClose on a pending widget removes the local entry.
  const handleAddWidget = async (template: WidgetTemplate) => {
    if (!selectedDashboard || isAdding) return;
    // Re-entry guard: rapid clicks on the same tile would otherwise queue
    // up multiple addPendingWidget dispatches since the body never awaited
    // a network call here (the modal closes via dispatch right after).
    setIsAdding(true);
    try {
      const size = getWidgetSize(template.type);
      const columns = selectedDashboard.columnCount || 24;
      const occupied = Object.keys(selectedDashboard.widgets)
        .map(
          (id) =>
            selectedDashboard.positioning[id] ??
            selectedDashboard.positioning[id.replace(/-/g, "")],
        )
        .filter((p): p is NonNullable<typeof p> => !!p);
      const slot = findFreeSlot(occupied, columns, size.w, size.h);
      const positioning = { x: slot.x, y: slot.y, w: size.w, h: size.h };

      const tempId = `pending-${crypto.randomUUID()}`;
      const pendingWidget: Widget = {
        id: tempId,
        title: template.title,
        type: template.type,
        config_type: template.config_type,
        // No query yet — the drawer wires machine/node/frequency/duration/
        // lookback/unit and saves them.
        query: null,
        encoding: template.encoding,
        semantics: template.semantics,
        presentation: template.presentation,
        template_id: template.id,
      };

      dispatch(
        addPendingWidget({
          dashboardId: selectedDashboard.id,
          widget: pendingWidget,
          positioning,
        }),
      );
      handleClose(); // modal unmounts on close
      dispatch(updateEditWidgetDrawerModalTileId(tempId));
      dispatch(
        updateModalsCloseDrawerModal({
          showModal: true,
          id: "editWidgetDrawerModal",
        }),
      );
      // No setIsAdding(false) on success: handleClose drops the modal and
      // the component unmounts, so the flag is gone with the next mount.
    } catch (e) {
      console.error("Add widget failed:", e);
      setIsAdding(false);
    }
  };

  return (
    <GlassyModal onClickOutside={handleClose}>
      <div className={styles[cssClassName]}>
        <div className={styles[`${cssClassName}__header`]}>
          <GlassyModalHeader
            title={title}
            rightIcon={closeIcon}
            rightIconFunction={handleClose}
          />
        </div>

        <div className={styles[`${cssClassName}__body`]}>
          <PillNavigation
            options={WIDGET_LIBRARY_CATEGORIES.map((c) => ({
              name: categoryLabel[c],
              link: c,
              render: true,
            }))}
            activeView={category}
            onChangeCallback={(value) =>
              setCategory(value as WidgetLibraryCategory)
            }
          />

          {isLoading ? (
            <div className={styles[`${cssClassName}__status`]}>
              <Spinner />
            </div>
          ) : error ? (
            <p className={styles[`${cssClassName}__status`]}>{error}</p>
          ) : categoryTemplates.length === 0 ? (
            <p className={styles[`${cssClassName}__status`]}>
              {i18n("pages.dashboardsPage.emptyCategory")}
            </p>
          ) : (
            <div
              className={styles[`${cssClassName}__tiles`]}
              style={
                isAdding ? { pointerEvents: "none", opacity: 0.6 } : undefined
              }
            >
              {categoryTemplates.map((t) => (
                <div
                  key={t.id}
                  className={styles[`${cssClassName}__tile`]}
                  onClick={() => handleAddWidget(t)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      handleAddWidget(t);
                    }
                  }}
                >
                  <WidgetRenderer
                    type={t.type}
                    title={t.title}
                    encoding={t.encoding}
                    semantics={t.semantics}
                    presentation={t.presentation}
                    preview
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </GlassyModal>
  );
};
